import React, { useEffect, useState } from 'react';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { useDispatch, useSelector } from 'react-redux';
import type { AnyAction } from 'redux';
import { getLocationDetails } from '@/redux/slices/location/locationDetails';
import AnalyticsPagesLayout from '@/components/analyticsPagesLayout';
import { AD_ANALYTICS_NAVBAR_ACTIONS, AD_CAMPAIGNS } from '../dashboard/config';
import AdCampaigns from './sections/adCampaigns';
import { getDefaultRanges } from '@/lomavisSharedUi/atoms/inputs/LomavisRangeCalendar1';
import CommonHeader from './sections/commonHeader';
import { PLATFORM_STATIC_ICONS_PNG } from '@/config/icons';
import { META } from '@/config/constants';
import { resolve } from '@/api/endPoints';
import axiosClient from '@/utils/axios';
import type { PaidCampaign } from '@/lomavisSharedUi/templates/sections/LomavisPaidAnalyticsView1/LomavisPaidAnalyticsView1';
import { formatDateAsISODateString } from '@/utils/dateUtils';
import {
  DEFAULT_CURRENCY,
  resolveCurrencyCode,
  type SupportedCurrency
} from './currency';
import { LomavisFeatureAvailable1 } from '@/lomavisSharedUi/atoms/links/LomavisFeatureAvailable1';

export const AdsAnalytics: React.FC = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { locationslug } = useParams();
  const defaultRanges = getDefaultRanges();

  const [dateRange, setDateRange] = useState({
    startDate: defaultRanges[2].startDate,
    endDate: defaultRanges[2].endDate
  });
  const [adAnalyticsLoading, setAdAnalyticsLoading] = useState(false);
  const [adAnalyticsError, setAdAnalyticsError] = useState<string | null>(null);
  const [adAnalyticsRaw, setAdAnalyticsRaw] = useState<any[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [currency, setCurrency] = useState<SupportedCurrency>(DEFAULT_CURRENCY);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [paidAdSub, setPaidAdSub] = useState(false);

  // Adjust selector to your slice shape
  const locationSlice = useSelector((s: any) => s.locationDetails);
  const locationUuid: string | undefined =
    locationSlice?.uuid ?? locationSlice?.data?.uuid ?? undefined;
  const locationLoading: boolean = Boolean(locationSlice?.isLoading);
  const locationError: string | null =
    locationSlice?.error?.message ?? locationSlice?.error ?? null;

  const meta_ad_accounts =
    locationSlice?.platforms_information?.meta_ad_account;

  const getSubscriptionData = async () => {
    setIsLoadingSubscription(true);
    try {
      const url = resolve('current_subscription', {}, {});
      const { data } = await axiosClient.get(url);

      setPaidAdSub(
        data?.social_media_management_subscription?.paid_ad_reporting
      );
    } catch (err: any) {
      console.error('Error refreshing competitor:', err);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  useEffect(() => {
    const accountCurrency =
      meta_ad_accounts && Array.isArray(meta_ad_accounts) && meta_ad_accounts[0]
        ? meta_ad_accounts[0].currency
        : null;
    setCurrency(resolveCurrencyCode(accountCurrency));
  }, [meta_ad_accounts]);

  useEffect(() => {
    if (locationslug) {
      dispatch(getLocationDetails(locationslug) as unknown as AnyAction);
      getSubscriptionData();
    }
  }, [dispatch, locationslug]);

  useEffect(() => {
    let cancelled = false;

    const fetchAdAnalytics = async () => {
      if (isLoadingSubscription || !paidAdSub) {
        return;
      }

      // Only attempt when we have a location and an ad account.
      if (!locationUuid || !meta_ad_accounts || meta_ad_accounts.length === 0) {
        return;
      }

      setAdAnalyticsLoading(true);
      setAdAnalyticsError(null);
      setAdAnalyticsRaw([]);

      try {
        let url = resolve(
          'ad_analytics',
          {},
          {
            location__uuid: locationUuid,
            start_date: formatDateAsISODateString(dateRange?.startDate),
            end_date: formatDateAsISODateString(dateRange?.endDate)
          }
        );
        const all: PaidCampaign[] = [];

        while (url && !cancelled) {
          const res = await axiosClient.get(url);
          const data = res?.data ?? {};
          if (cancelled) return;

          if (Array.isArray(data.results))
            all.push(...data.results?.map((item: any) => item?.data));
          url = data.next ?? null;
        }

        if (!cancelled) setAdAnalyticsRaw(all);
      } catch (e: any) {
        if (!cancelled) {
          console.error('Error fetching paid analytics:', e);
          setAdAnalyticsError(
            e?.message ||
              t('errors.failedToFetch', 'Failed to fetch paid analytics.')
          );
        }
      } finally {
        if (!cancelled) setAdAnalyticsLoading(false);
      }
    };

    fetchAdAnalytics();

    return () => {
      cancelled = true;
    };
  }, [
    locationUuid,
    meta_ad_accounts,
    dateRange,
    reloadKey,
    paidAdSub,
    isLoadingSubscription
  ]);

  const navBarHeight =
    document.getElementById('kt_app_header')?.clientHeight + 15 * 2 + 50;

  // --- Location gating UI ---
  if (locationLoading && !locationUuid) {
    return (
      <div
        className="p-3 w-100 d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: 240 }}
      >
        <Spinner animation="border" role="status" />
        <div className="mt-2 small text-muted">
          {t('lomavisAdAnalytics.loadingLocation', 'Loading location…')}
        </div>
      </div>
    );
  }

  if (!locationUuid && locationError) {
    return (
      <Container
        style={{
          height: `calc(100vh - ${navBarHeight}px)`
        }}
        className="w-100"
      >
        <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
          <Alert variant="danger" className="mb-3 w-100 ">
            <div className="fs-3 text-center">
              {t('adsAnalytics.locationErrorTitle', "Couldn't load location")}
            </div>
          </Alert>
          {locationslug && (
            <Button
              variant="outline-primary"
              onClick={() =>
                dispatch(
                  getLocationDetails(locationslug) as unknown as AnyAction
                )
              }
            >
              {t('common.retry', 'Retry')}
            </Button>
          )}
        </div>
      </Container>
    );
  }

  if (isLoadingSubscription) {
    return (
      <Container
        style={{
          height: `calc(100vh - ${navBarHeight}px)`
        }}
        className="w-100"
      >
        <div className="w-100 h-100 d-flex justify-content-center align-items-center ">
          <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-3 justify-content-center align-items-center">
            <Spinner animation="border" role="status" />
            <div className="mt-2 small text-muted">
              {t('adsAnalytics.loadingSubscription', 'Checking subscription…')}
            </div>
          </div>
        </div>
      </Container>
    );
  }

  if (!meta_ad_accounts || meta_ad_accounts?.length === 0) {
    return (
      <Container
        style={{
          height: `calc(100vh - ${navBarHeight}px)`
        }}
        className="w-100"
      >
        <div className="w-100 h-100 d-flex justify-content-center align-items-center ">
          <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
            <span className="fs-3">{t('adsAnalytics.noAdAccount')}</span>
            <div className="d-flex gap-3 justify-content-center">
              {' '}
              <img
                src={PLATFORM_STATIC_ICONS_PNG[META]}
                className="h-25px"
                style={{ marginRight: 2 }}
              />
            </div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container
      style={{
        height: `calc(100vh - ${navBarHeight}px)`
      }}
      className="w-100"
    >
      <LomavisFeatureAvailable1
        isAvailable={paidAdSub}
        title={t('adsAnalytics.notAvailable')}
      >
        <AnalyticsPagesLayout
          actions={AD_ANALYTICS_NAVBAR_ACTIONS}
          commonHeader={
            <CommonHeader
              setDate={(date) => {
                setDateRange(date);
              }}
            />
          }
          tabs={{
            [AD_CAMPAIGNS]: (
              <AdCampaigns
                key={locationUuid}
                dateRange={dateRange}
                rawData={adAnalyticsRaw}
                loading={adAnalyticsLoading}
                error={adAnalyticsError}
                onRetry={() => setReloadKey((k) => k + 1)}
                currency={currency}
              />
            )
          }}
        />
      </LomavisFeatureAvailable1>
    </Container>
  );
};

export default AdsAnalytics;
